import { z } from "zod";
import { responseSchema } from "../http.js";
import { orderProofInputSchema, proofMimeSchema } from "./order.js";
/**
 * ═══════════════════════════════════════════════════════════
 * KYC — la verificación de identidad
 *
 * (Anahí, 27-ago): el usuario sube sus documentos desde su panel; el
 * super-admin los VE en `/admin/users` y aprueba o rechaza. El KYC será
 * requisito para pagar comisiones — por eso el estado vive aquí y no en
 * una nota.
 *
 * ── El ciclo ───────────────────────────────────────────────
 * `not_started` → (sube su primer documento) → `pending_review` →
 * `approved` | `rejected`. Un rechazo lleva NOTA: "rechazado" a secas
 * obliga a adivinar qué corregir. Subir un documento nuevo después de
 * un rechazo vuelve a `pending_review` — corregir reabre la revisión.
 *
 * ── Los archivos ───────────────────────────────────────────
 * El mismo transporte que el comprobante de pago: base64 en JSON, los
 * mismos formatos (foto o PDF) y el mismo techo de 4 MB. El contenido
 * (`dataUrl`) solo viaja en el DETALLE que pide el admin — en los
 * listados sería megabytes por fila.
 * ═══════════════════════════════════════════════════════════
 */
export const KYC_STATUSES = [
    "not_started",
    "pending_review",
    "approved",
    "rejected",
];
export const kycStatusSchema = z.enum(KYC_STATUSES);
export const KYC_STATUS_LABELS = {
    not_started: "Sin documentos",
    pending_review: "En revisión",
    approved: "Aprobado",
    rejected: "Rechazado",
};
/** Qué documento es. La lista de producción: identificación por las dos
 * caras y un comprobante de domicilio. */
export const KYC_DOCUMENT_KINDS = ["id_front", "id_back", "proof_of_address"];
export const kycDocumentKindSchema = z.enum(KYC_DOCUMENT_KINDS);
export const KYC_DOCUMENT_KIND_LABELS = {
    id_front: "Identificación · frente",
    id_back: "Identificación · reverso",
    proof_of_address: "Comprobante de domicilio",
};
/** Lo que sube el usuario: el tipo + el archivo (mismo sobre que el
 * comprobante de pago). Un tipo repetido REEMPLAZA al anterior. */
export const kycDocumentInputSchema = orderProofInputSchema.extend({
    kind: kycDocumentKindSchema,
});
export const kycDocumentSchema = z.object({
    kind: kycDocumentKindSchema,
    fileName: z.string(),
    mimeType: proofMimeSchema,
    sizeBytes: z.number().int().min(0),
    uploadedAt: z.iso.datetime(),
    /** Solo en el detalle del admin. */
    dataUrl: z.string().optional(),
});
export const kycSchema = z.object({
    userId: z.string(),
    status: kycStatusSchema,
    documents: z.array(kycDocumentSchema),
    /** La última vez que el usuario mandó algo a revisión. */
    submittedAt: z.iso.datetime().optional(),
    reviewedAt: z.iso.datetime().optional(),
    /** La nota del rechazo — o del visto bueno, si la hubo. */
    reviewNote: z.string().optional(),
});
/** El veredicto del panel. Rechazar EXIGE la nota. */
export const kycReviewSchema = z
    .object({
    status: z.enum(["approved", "rejected"]),
    note: z.string().trim().max(500).optional(),
})
    .superRefine((value, ctx) => {
    if (value.status === "rejected" && !value.note?.trim()) {
        ctx.addIssue({
            code: "custom",
            path: ["note"],
            message: "Escribe qué debe corregir: 'rechazado' a secas no ayuda a nadie",
        });
    }
});
export const kycResponseSchema = responseSchema(z.object({ kyc: kycSchema }));
export const KYC_ROUTES = {
    /** El propio: ver y subir. */
    mine: "/api/kyc/me",
    mineDocuments: "/api/kyc/me/documents",
    /** El panel: el expediente CON archivos, y el veredicto. */
    adminOne: (userId) => `/api/admin/users/${userId}/kyc`,
};
/**
 * ¿Esta cuenta tiene que subir documentos? (Anahí, 22-oct-2026)
 *
 * «Cuando sea el país de Estados Unidos no se pidan documentos.» Se
 * decide por el país de la CUENTA (no el de conexión): el expediente es
 * de la persona, no del lugar desde donde abre la página.
 */
export function requiresKyc(country) {
    return country !== "US";
}
//# sourceMappingURL=kyc.js.map